import { useState } from "react";
import { DSBaseTable, DSTableCell } from "../../system-design/Table";
import { Column } from "../../system-design/Table/DSBaseTable/components/DSBaseTable";
import DSBox from "../../system-design/DSBox";
import DSCard from "../../system-design/DSCard";
import DSCardContent from "../../system-design/DSCardContent";
import DSTypography from "../../system-design/DSTypography";
import DSTextField from "../../system-design/DSTextField";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import { TestSerialEntry } from "../../../api/serials";
import { extractApiErrorMessage } from "../../../utils/util";
import useTestSerials from "../hooks/useTestSerials";
import useAddTestSerialMutation from "../hooks/useAddTestSerialMutation";

const columns: Column<TestSerialEntry>[] = [
  { label: "Serial", field: "serialNumber" },
  { label: "Friendly identifier", field: "friendlyIdentifier" },
  { label: "Note", field: "note" },
  { label: "Added", field: "addedAt" },
  { label: "Added by", field: "addedBy" },
];

const formatDate = (iso: string) => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
};

const row = (entry: TestSerialEntry, cols: Column<TestSerialEntry>[]) => (
  <>
    {cols.map((col, index) => {
      const key = `${col.label}-${index}`;
      if (col.field === "addedAt") {
        return <DSTableCell key={key}>{formatDate(entry.addedAt)}</DSTableCell>;
      }
      return (
        <DSTableCell key={key}>
          {(entry[col.field as keyof TestSerialEntry] as string | null) || "—"}
        </DSTableCell>
      );
    })}
  </>
);

// Serials made for testing, outside batch generation. Batch generation never
// issues a serial or friendly identifier that is in this list.
const TestSerialsCard = () => {
  const [serial, setSerial] = useState("");
  const [note, setNote] = useState("");
  const list = useTestSerials();
  const add = useAddTestSerialMutation();

  const submit = () => {
    if (!serial.trim() || add.isPending) return;
    add.mutate(
      { serial: serial.trim(), note: note.trim() || undefined },
      {
        onSuccess: () => {
          setSerial("");
          setNote("");
        },
      },
    );
  };

  return (
    <DSCard sx={{ mb: 3 }}>
      <DSCardContent>
        <DSTypography variant="h6" sx={{ mb: 0.5 }}>
          Test serials
        </DSTypography>
        <DSTypography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
          Serials made for testing. Batch generation never issues a serial or
          friendly identifier from this list.
        </DSTypography>
        <DSBox sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <DSTextField
            value={serial}
            onChange={(e) => setSerial(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            placeholder="Serial"
            sx={{ flex: 1, minWidth: 220 }}
          />
          <DSTextField
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            placeholder="Note (optional)"
            sx={{ flex: 1, minWidth: 220 }}
          />
          <DSPrimaryButton
            onClick={submit}
            disabled={!serial.trim() || add.isPending}
          >
            {add.isPending ? "Adding…" : "Add test serial"}
          </DSPrimaryButton>
        </DSBox>

        {add.isError && (
          <DSTypography variant="body2" color="error" sx={{ mt: 1.5 }}>
            {extractApiErrorMessage(add.error, "Failed to add the test serial")}
          </DSTypography>
        )}

        <DSBox sx={{ mt: 2 }}>
          {/* A failed load must not look like an empty list. */}
          {list.isError && (
            <DSTypography variant="body2" color="error">
              {extractApiErrorMessage(
                list.error,
                "Failed to load the test serials",
              )}
            </DSTypography>
          )}
          {list.isPending && <DSCircularProgress size={22} />}
          {list.data &&
            (list.data.length > 0 ? (
              <DSBaseTable
                data={[...list.data].reverse()}
                columns={columns}
                rowBuilder={row}
              />
            ) : (
              <DSTypography variant="body2" color="textSecondary">
                No test serials yet.
              </DSTypography>
            ))}
        </DSBox>
      </DSCardContent>
    </DSCard>
  );
};

export default TestSerialsCard;
